import { computed, Directive, effect, inject, resource, signal, Signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  ApiException,
  FormDataDto,
  FormSchemaMiniDto,
  FormTemplateClient,
  isUnityValidationProblem,
  TransitionMiniDto,
  UnityValidationError,
  WorkflowsClient,
} from '@unity/clients';
import { FormSchemaService } from '@unity/services/form-schema.service';
import { NavigationService } from '@unity/services/navigation.service';
import { ToasterService } from '@unity/services/toaster.service';
import { CanDeactivateGuard, UnityRouteUrlTrees } from '@unity/utils/types';
import { catchError, EMPTY, Observable, take } from 'rxjs';
import { SurveyModel } from 'survey-core';

@Directive() // See https://github.com/angular/angular/issues/37769
export abstract class AbstractNewSurveyComponent<TResponseDto> implements CanDeactivateGuard {
  private readonly workflowsClientExpense = inject(WorkflowsClient);
  private readonly formSchemasClient = inject(FormTemplateClient);
  private readonly formSchemaService = inject(FormSchemaService);
  private readonly navigationService = inject(NavigationService);
  private readonly toasterService = inject(ToasterService);

  /* Abstract */
  protected abstract readonly $formSchema: Signal<FormSchemaMiniDto | null | undefined>;

  protected abstract redirectUrlFn: (response: TResponseDto) => UnityRouteUrlTrees;
  protected abstract cancelUrlFn: () => UnityRouteUrlTrees;

  protected abstract createResourceFn$: (data: FormDataDto, transition: TransitionMiniDto) => Observable<TResponseDto>;

  /* Signals */
  protected readonly $isCreating = signal<boolean>(false);
  protected readonly $isDirty = signal<boolean>(false);

  /* Computed */
  protected readonly $surveyModel = computed(() => this.surveyModelResource.value());

  protected readonly $initialTransitions = computed(() => {
    if (!this.workflowResource.hasValue()) {
      return [];
    }
    const workflow = this.workflowResource.value();
    return workflow.workflow_transitions.filter(x => x.is_initial);
  });

  /* Resources */
  private readonly formSchemaResource = rxResource({
    params: () => {
      const formSchema = this.$formSchema();
      const formSchemaId = formSchema?.id;
      return formSchemaId ? { formSchemaId } : undefined;
    },
    stream: ({ params: { formSchemaId } }) => this.formSchemasClient.getOneAsDetailed(formSchemaId),
  });

  private readonly surveyModelResource = resource({
    params: () => {
      const formSchema = this.formSchemaResource.value();
      const formSchemaData = formSchema?.data;
      return formSchemaData ? { formSchemaData } : undefined;
    },
    loader: ({ params: { formSchemaData } }) => this.formSchemaService.surveyModel(formSchemaData),
  });

  private readonly workflowResource = rxResource({
    params: () => {
      const formSchema = this.formSchemaResource.value();
      const workflowId = formSchema?.default_workflow.id;
      return workflowId ? { workflowId } : undefined;
    },
    stream: ({ params: { workflowId } }) => this.workflowsClientExpense.getOneAsDetailed(workflowId),
  });

  /* Methods */
  public constructor() {
    effect(() => {
      const surveyModel = this.$surveyModel();
      if (surveyModel) {
        surveyModel.onValueChanged.add(() => {
          this.$isDirty.set(true);
        });
      }
    });
  }

  protected onCancel() {
    this.$isDirty.set(false);
    const redirectUrl = this.cancelUrlFn();
    this.navigationService.navigate(redirectUrl);
  }

  protected onCreate(transition: TransitionMiniDto) {
    const surveyModel = untracked(this.$surveyModel);

    if (!surveyModel) {
      return;
    }

    surveyModel.clearIncorrectValues(true);

    if (transition.requires_validation) {
      surveyModel.validate(true, true, hasErrors => {
        if (!hasErrors) {
          this.createSurvey(surveyModel, transition);
        }
      });
      return;
    }

    this.createSurvey(surveyModel, transition);
  }

  private createSurvey(surveyModel: SurveyModel, transition: TransitionMiniDto) {
    surveyModel.readOnly = true;
    this.$isCreating.set(true);

    this.createResourceFn$(surveyModel.data, transition)
      .pipe(
        catchError(e => {
          return this.onCreateError(surveyModel, e);
        }),
        take(1)
      )
      .subscribe({
        next: res => {
          this.onCreateSuccess(res);
        },
      });
  }

  private onCreateSuccess(res: TResponseDto) {
    this.toasterService.showInfo('Saved');
    const redirectUrl = this.redirectUrlFn(res);
    this.navigationService.navigate(redirectUrl, { queryParams: { success: true } });
  }

  private onCreateError(surveyModel: SurveyModel, e: Error) {
    if (ApiException.isApiException(e) && isUnityValidationProblem(e.result)) {
      //handle server side errros
      this.handleServerValidationErrors(surveyModel, e.result.errors ?? {});
    } else {
      this.toasterService.showError('Error while saving changes', e, 'error');
    }

    surveyModel.readOnly = false;
    this.$isCreating.set(false);
    return EMPTY;
  }

  private handleServerValidationErrors(surveyModel: SurveyModel, errors: Record<string, UnityValidationError[]>) {
    if (errors['']) {
      this.toasterService.showError('Form invalid', new Error(errors[''].join('. ')), 'warning');
    }

    for (const key of Object.keys(errors)) {
      const question = surveyModel.getQuestionByName(key);
      const errorMessage = errors[key].map(x => x.message).join('. ');
      if (question) {
        question.addError(errorMessage);
      } else {
        this.toasterService.showError('Form invalid', new Error(errorMessage), 'warning');
      }
    }
  }

  public canDeactivate() {
    const isDirty = untracked(this.$isDirty);

    if (isDirty) {
      return window.confirm('You have unsaved changes on this page. Are you sure you want to leave?');
    }

    return true;
  }
}
